// Ordered list of audit action types for the audit-log filter dropdown.
// These values mirror the audit action strings written by the server
// (see server/services + routes). The server validates `actionType`, so this
// list must stay in sync with the set of actions the backend recognizes.

export interface AuditActionOption {
    value: string;
    label: string;
}

export const AUDIT_ACTION_OPTIONS: AuditActionOption[] = [
    { value: 'photo_uploaded', label: 'Photo Uploaded' },
    { value: 'photo_approved', label: 'Photo Approved' },
    { value: 'photo_rejected', label: 'Photo Rejected' },
    { value: 'auto_approved', label: 'Auto Approved' },
    { value: 'admin_tagged_photo', label: 'Admin Tagged Photo' },
    { value: 'group_tagged_auto_approved', label: 'Group Tagged (Auto Approved)' },
    { value: 'ai_tagged_photo', label: 'AI Tagged Photo' },
    { value: 'gallery_accessed', label: 'Gallery Accessed' },
    { value: 'review_gallery_opened', label: 'Review Gallery Opened' },
    { value: 'approval_email_sent', label: 'Approval Email Sent' },
    { value: 'approval_email_failed', label: 'Approval Email Failed' },
    { value: 'batch_upload_notification_sent', label: 'Batch Upload Notification Sent' },
    { value: 'batch_upload_notification_failed', label: 'Batch Upload Notification Failed' },
    { value: 'ad_selections_submitted', label: 'AD Selections Submitted' },
    { value: 'ad_selections_updated', label: 'AD Selections Updated' },
    { value: 'ad_selections_notification_sent', label: 'AD Selections Notification Sent' },
    { value: 'ad_selections_notification_failed', label: 'AD Selections Notification Failed' },
    { value: 'review_window_closed', label: 'Review Window Closed' },
    { value: 'secondary_batch_closed', label: 'Secondary Batch Closed' },
    { value: 'ad_zero_selection_processed', label: 'AD Zero Selection Processed' },
    { value: 'single_point_selections_approved', label: 'Single Point Selections Approved' },
    { value: 'batch_marked_processed_single_point', label: 'Batch Marked Processed (Single Point)' },
    { value: 'tag_correction_initiated', label: 'Tag Correction Initiated' },
    { value: 'tag_correction_credit_removed', label: 'Tag Correction Credit Removed' },
    { value: 'tag_correction_credit_added', label: 'Tag Correction Credit Added' },
    { value: 'photo_selected', label: 'Photo Selected' },
    { value: 'photo_deselected', label: 'Photo Deselected' },
    { value: 'batch_committed', label: 'Batch Committed' },
    { value: 'batch_exported', label: 'Batch Exported' },
    { value: 'photo_downloaded', label: 'Photo Downloaded' },
    { value: 'photo_deleted', label: 'Photo Deleted' },
    { value: 'batch_deleted', label: 'Batch Deleted' },
];

// value -> human-friendly label, for rendering the Action column etc.
export const AUDIT_ACTION_LABELS: Record<string, string> = Object.fromEntries(
    AUDIT_ACTION_OPTIONS.map(o => [o.value, o.label]),
);

// Actions that are always scoped to a specific photo (logged with a photoId).
// A null photoId on one of these means the photo row was later deleted.
// Any action NOT in this set is batch-level / non-photo and must render "—"
// in the Photo column — never "(deleted photo)".
export const PHOTO_SCOPED_ACTIONS: Set<string> = new Set([
    'photo_uploaded',
    'photo_approved',
    'photo_rejected',
    'auto_approved',
    'admin_tagged_photo',
    'group_tagged_auto_approved',
    'ai_tagged_photo',
    'tag_correction_initiated',
    'tag_correction_credit_removed',
    'tag_correction_credit_added',
    'photo_selected',
    'photo_deselected',
    'photo_downloaded',
    'photo_deleted',
]);
